import { Assessment, Group, PlayArrow, Speed } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

const API_BASE = process.env.REACT_APP_API_URL;

function Dashboard() {
  const theme = useTheme();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(moment.tz("America/New_York").toDate());
  const [dashboardData, setDashboardData] = useState({
    totalAreas: 0,
    totalReleases: 0,
    totalUsers: 0,
    environmentDistribution: [],
    areaReleaseDistribution: [],
    recentReleases: [],
    monthlyTrend: [],
    teamDistribution: [],
  });

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/areas`);
      if (!res.ok) throw new Error("Failed to fetch areas");

      const areasData = await res.json();
      setAreas(areasData);

      // Process data for dashboard
      const totalAreas = areasData.length;
      const totalReleases = areasData.reduce(
        (sum, area) => sum + (area.releases?.length || 0),
        0
      );

      // Count unique users
      const uniqueUsers = new Set();
      areasData.forEach((area) => {
        area.releases?.forEach((release) => {
          release.users?.forEach((user) => {
            uniqueUsers.add(user.email);
          });
        });
      });
      const totalUsers = uniqueUsers.size;

      // Environment distribution
      const envCount = {};
      areasData.forEach((area) => {
        area.releases?.forEach((release) => {
          release.environments?.forEach((env) => {
            envCount[env] = (envCount[env] || 0) + 1;
          });
        });
      });

      const environmentDistribution = Object.entries(envCount).map(
        ([env, count]) => ({
          name: env,
          value: count,
          percentage: Math.round((count / totalReleases) * 100),
          color:
            env === "PROD" ? "#e74c3c" : env === "QA" ? "#3498db" : "#f39c12",
        })
      );

      // Area release distribution with colors
      const areaReleaseDistribution = areasData.map((area, index) => ({
        name: area.areaName,
        releases: area.releases?.length || 0,
        fill: `hsl(${(index * 137.5) % 360}, 70%, 60%)`,
      }));

      // Recent releases (last 6)
      const allReleases = areasData.flatMap((area) =>
        (area.releases || []).map((release) => ({
          ...release,
          areaName: area.areaName,
        }))
      );

      const recentReleases = allReleases
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 6);

      // Monthly trend (last 6 months)
      const monthlyData = {};
      const last6Months = Array.from({ length: 6 }, (_, i) => {
        const date = moment.tz("America/New_York").subtract(i, "months");
        return date.format("YYYY-MM");
      }).reverse();

      last6Months.forEach((month) => {
        monthlyData[month] = { month: month.slice(5), releases: 0, areas: 0 };
      });

      allReleases.forEach((release) => {
        const month = release.createdAt?.slice(0, 7);
        if (monthlyData[month]) {
          monthlyData[month].releases++;
        }
      });

      const monthlyTrend = Object.values(monthlyData);

      // Team distribution
      const teamCount = {};
      allReleases.forEach((release) => {
        release.users?.forEach((user) => {
          teamCount[user.name] = (teamCount[user.name] || 0) + 1;
        });
      });

      const teamDistribution = Object.entries(teamCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, releases: count }));

      setDashboardData({
        totalAreas,
        totalReleases,
        totalUsers,
        environmentDistribution,
        areaReleaseDistribution,
        recentReleases,
        monthlyTrend,
        teamDistribution,
      });

      setLastUpdated(moment.tz("America/New_York").toDate());
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}10 0%, ${color}20 100%)`,
        border: `1px solid ${color}30`,
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 25px ${color}30`,
          transition: "all 0.3s ease",
        },
      }}
    >
      <CardContent sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color, mb: 0.5 }}>
              {value}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Chip
                label={trend}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: `${color}20`,
                  color: color,
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: color,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${color}40`,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
      {/* Decorative gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: `linear-gradient(45deg, ${color}20, transparent)`,
          zIndex: 0,
        }}
      />
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: "1px solid #e0e0e0" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (loading && dashboardData.totalAreas === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="releases-container">
      <Box className="releases-header">
        <Typography variant="h5" className="page-title">
          Deployment Tracker Dashboard
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      ></Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Areas"
            value={dashboardData.totalAreas}
            icon={<Assessment fontSize="large" />}
            color="#667eea"
            subtitle="Active deployment areas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Planned Deployments"
            value={dashboardData.totalReleases}
            icon={<PlayArrow fontSize="large" />}
            color="#10b981"
            subtitle="Across all areas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Team Members"
            value={dashboardData.totalUsers}
            icon={<Group fontSize="large" />}
            color="#f59e0b"
            subtitle="Active contributors"
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Environment Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Environment Distribution
            </Typography>
            {dashboardData.environmentDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.environmentDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                  >
                    {dashboardData.environmentDistribution.map(
                      (entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      )
                    )}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography color="text.secondary">
                  No environment data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Monthly Trend */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Deployment Trend (Last 6 Months)
            </Typography>
            {dashboardData.monthlyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData.monthlyTrend}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="releases"
                    stroke="#667eea"
                    fill="url(#colorGradient)"
                    strokeWidth={2}
                  />
                  <defs>
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#667eea"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography color="text.secondary">
                  No trend data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid> */}
      </Grid>
    </Box>
  );
}

export default Dashboard;
